// Port of Flutter's globals.dart showMessageDialog(context, title, message):
// a centred, bold title over scrollable body text on the app background colour,
// with a single OK button in the button colour.
//
// Flutter can pop a dialog from anywhere because it has a BuildContext; React
// needs a provider, so mount <MessageDialogProvider> once in the mobile shell
// and call useMessage().show(title, body) from anywhere below it.
//
// ⚠️ NO OK CLICK (owner, 2026-10-20): show() is now a TOAST that closes itself
// (ui/shared/toast) — "Name Updated", "Wi-Fi Sent", "Invalid Number" all only
// inform, and an OK on each was one extra tap. Only a text meant to be READ
// keeps the dialog: show(title, body, { sticky: true }) (the terms).

import React, {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, useTheme,
} from '@mui/material';

import { showToast } from './toast';

const Ctx = createContext({ show: () => {} });

/** The toast's colour from the title's words — a failure must not look like a success. */
export function severityOf(title) {
  const t = String(title || '').toLowerCase();
  if (/fail|invalid|can.?t|couldn.?t|could not|not |no |wrong|error|did not|needed|missing|already/.test(t)) return 'error';
  if (/updated|added|saved|sent|registered|done|success/.test(t)) return 'success';
  return 'info';
}

export function MessageDialogProvider({ children }) {
  const theme = useTheme();
  const c = theme.sli;
  const [msg, setMsg] = useState(null);

  const show = useCallback((title, message, opts = {}) => {
    if (opts.sticky) { setMsg({ title, message }); return; }
    showToast([title, message].filter(Boolean).join('\n'), { severity: severityOf(title) });
  }, []);
  const value = useMemo(() => ({ show }), [show]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <Dialog
        open={!!msg}
        onClose={() => setMsg(null)}
        PaperProps={{ sx: { bgcolor: c.back, borderRadius: '16px', minWidth: 280 } }}
      >
        <DialogTitle sx={{ textAlign: 'center', color: c.text, fontWeight: 700 }}>
          {msg?.title}
        </DialogTitle>
        <DialogContent sx={{ maxHeight: 360, overflowY: 'auto' }}>
          <Typography sx={{ color: c.text, fontSize: 16, whiteSpace: 'pre-line' }}>
            {msg?.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            onClick={() => setMsg(null)}
            sx={{
              bgcolor: c.button,
              color: c.text,
              borderRadius: '10px',
              px: 2.5,
              py: 1.5,
              letterSpacing: '1.2px',
              fontWeight: 600,
              '&:hover': { bgcolor: c.button },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Ctx.Provider>
  );
}

export function useMessage() {
  return useContext(Ctx);
}
